import { useState, useEffect, useRef, RefObject, useCallback } from 'react';
import { Flex, Text } from '@servicetitan/anvil2';
import { Calculation, PaymentFrequency } from '@servicetitan/financing-api-client';

// Define the specific payment frequencies used in the UI
type UIPaymentFrequency =
    | PaymentFrequency.Weekly
    | PaymentFrequency.BiWeekly
    | PaymentFrequency.Monthly;

export interface PaymentFrequencyTabsProps {
    plans: Calculation[];
    onFrequencyChange: (frequency: PaymentFrequency) => void;
    scrollContainerRef?: RefObject<HTMLDivElement>;
}

/**
 * Always displays tabs for Weekly, Bi-Weekly, and Monthly payment frequencies
 * Tracks the selected frequency and notifies parent for rendering decisions
 * Shows "no plans available" message when no plans match the selected frequency
 * Manages independent scroll positions for each tab
 */
export const PaymentFrequencyTabs = ({
    plans,
    onFrequencyChange,
    scrollContainerRef,
}: PaymentFrequencyTabsProps) => {
    const [selectedFrequency, setSelectedFrequency] = useState<UIPaymentFrequency>(
        PaymentFrequency.Weekly,
    );
    const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
    const [hasRendered, setHasRendered] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const tabRefs = useRef<Partial<Record<PaymentFrequency, HTMLButtonElement | null>>>({
        [PaymentFrequency.Weekly]: null,
        [PaymentFrequency.BiWeekly]: null,
        [PaymentFrequency.Monthly]: null,
    });
    const hasInitialized = useRef(false);

    // Store scroll positions for each frequency tab
    const scrollPositions = useRef<Partial<Record<PaymentFrequency, number>>>({
        [PaymentFrequency.Weekly]: 0,
        [PaymentFrequency.BiWeekly]: 0,
        [PaymentFrequency.Monthly]: 0,
    });

    // Throttle scroll events to prevent excessive calls
    const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Update sliding indicator position
    const updateIndicatorPosition = useCallback((frequency: UIPaymentFrequency) => {
        const selectedTab = tabRefs.current[frequency];
        const container = containerRef.current;

        if (selectedTab && container) {
            const containerRect = container.getBoundingClientRect();
            const tabRect = selectedTab.getBoundingClientRect();

            setIndicatorStyle({
                left: tabRect.left - containerRect.left,
                width: tabRect.width,
            });
        }
    }, []);

    // Set up scroll event listener
    useEffect(() => {
        const scrollContainer = scrollContainerRef?.current;
        if (!scrollContainer) {
            return;
        }

        // Handle scroll events with throttling
        const handleScroll = () => {
            if (!scrollContainerRef?.current) {
                return;
            }

            // Clear existing timeout
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }

            // Throttle scroll position saving
            scrollTimeoutRef.current = setTimeout(() => {
                if (scrollContainerRef.current) {
                    const currentPosition = scrollPositions.current[selectedFrequency];
                    if (currentPosition !== undefined) {
                        scrollPositions.current[selectedFrequency] =
                            scrollContainerRef.current.scrollLeft;
                    }
                }
            }, 100);
        };

        scrollContainer.addEventListener('scroll', handleScroll);
        return () => {
            scrollContainer.removeEventListener('scroll', handleScroll);
        };
    }, [scrollContainerRef, selectedFrequency]);

    // Handle frequency tab selection
    const handleFrequencyChange = (frequency: UIPaymentFrequency) => {
        if (frequency === selectedFrequency) {
            return; // Prevent redundant calls
        }

        // Save current scroll position before switching
        if (scrollContainerRef?.current) {
            const currentPosition = scrollPositions.current[selectedFrequency];
            if (currentPosition !== undefined) {
                scrollPositions.current[selectedFrequency] = scrollContainerRef.current.scrollLeft;
            }
        }

        setSelectedFrequency(frequency);
        updateIndicatorPosition(frequency);
        onFrequencyChange(frequency);

        // Restore scroll position for the new frequency after a short delay
        if (scrollContainerRef?.current) {
            setTimeout(() => {
                if (scrollContainerRef.current) {
                    const savedPosition = scrollPositions.current[frequency];
                    if (savedPosition !== undefined) {
                        scrollContainerRef.current.scrollLeft = savedPosition;
                    }
                }
            }, 50);
        }
    };

    // Initialize on first load only
    useEffect(() => {
        if (plans.length > 0 && !hasInitialized.current) {
            hasInitialized.current = true;
            onFrequencyChange(selectedFrequency);
        }
    }, [plans.length, onFrequencyChange, selectedFrequency]);

    // Update indicator position when component mounts or selectedFrequency changes
    useEffect(() => {
        updateIndicatorPosition(selectedFrequency);
    }, [selectedFrequency, updateIndicatorPosition]);

    // Enable transitions after initial positioning is complete
    useEffect(() => {
        const timer = setTimeout(() => {
            setHasRendered(true);
        }, 0);
        return () => clearTimeout(timer);
    }, []);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    // Custom tab button styles
    const tabStyles = {
        padding: '6px',
        borderRadius: '6px',
        border: 'none',
        cursor: 'pointer',
        backgroundColor: 'transparent',
        flex: 1,
        textAlign: 'center' as const,
        position: 'relative' as const,
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };

    return (
        <Flex direction="column" gap="3">
            <div
                ref={containerRef}
                style={{
                    position: 'relative',
                    backgroundColor: '#F5F5F5',
                    padding: '6px',
                    borderRadius: '6px',
                    width: '100%',
                    display: 'flex',
                    gap: '2px',
                }}
            >
                {/* Sliding indicator */}
                <div
                    style={{
                        position: 'absolute',
                        top: '4px',
                        left: `${indicatorStyle.left}px`,
                        width: `${indicatorStyle.width}px`,
                        height: 'calc(100% - 8px)',
                        backgroundColor: '#FFFFFF',
                        borderRadius: '4px',
                        transition: hasRendered ? 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
                        zIndex: 1,
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
                    }}
                />

                <button
                    ref={el => (tabRefs.current[PaymentFrequency.Weekly] = el)}
                    style={tabStyles}
                    onClick={() => handleFrequencyChange(PaymentFrequency.Weekly)}
                >
                    <Text
                        color={
                            selectedFrequency === PaymentFrequency.Weekly ? 'neutral' : 'subdued'
                        }
                        style={{ fontSize: '16px', fontWeight: 600 }}
                    >
                        Weekly
                    </Text>
                </button>
                <button
                    ref={el => (tabRefs.current[PaymentFrequency.BiWeekly] = el)}
                    style={tabStyles}
                    onClick={() => handleFrequencyChange(PaymentFrequency.BiWeekly)}
                >
                    <Text
                        variant="body"
                        size="small"
                        color={
                            selectedFrequency === PaymentFrequency.BiWeekly ? 'neutral' : 'subdued'
                        }
                        style={{ fontSize: '16px', fontWeight: 600 }}
                    >
                        Bi-Weekly
                    </Text>
                </button>
                <button
                    ref={el => (tabRefs.current[PaymentFrequency.Monthly] = el)}
                    style={tabStyles}
                    onClick={() => handleFrequencyChange(PaymentFrequency.Monthly)}
                >
                    <Text
                        variant="body"
                        size="small"
                        color={
                            selectedFrequency === PaymentFrequency.Monthly ? 'neutral' : 'subdued'
                        }
                        style={{ fontSize: '16px', fontWeight: 600 }}
                    >
                        Monthly
                    </Text>
                </button>
            </div>
        </Flex>
    );
};
